import React, { useId } from 'react';
import { Upload } from 'lucide-react';
import { cn } from '../lib/utils';

type InputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'>;
type LabelProps = Omit<React.LabelHTMLAttributes<HTMLLabelElement>, 'onChange'>;

interface ImageUploadDropzoneProps extends LabelProps {
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  accept?: string;
  multiple?: boolean;
  helperText?: string;
  secondaryText?: string;
  primaryText?: string;
  icon?: React.ReactNode;
  containerClassName?: string;
  inputProps?: InputProps;
  inputId?: string;
  dataTestId?: string;
  children?: React.ReactNode;
}

/**
 * Shared clickable dropzone for image uploads. Wraps a visually rich label that
 * forwards click and drag interactions to an underlying hidden file input.
 */
export const ImageUploadDropzone: React.FC<ImageUploadDropzoneProps> = ({
  onChange,
  accept = 'image/*',
  multiple = false,
  helperText = 'PNG, JPG, GIF up to 5MB',
  secondaryText = 'or drag and drop',
  primaryText = 'Click to upload',
  icon,
  className,
  containerClassName,
  inputProps,
  inputId,
  dataTestId,
  disabled,
  children,
  ...labelProps
}) => {
  const generatedId = useId();
  const resolvedInputId = inputId ?? `image-upload-${generatedId}`;

  const renderedIcon =
    icon ??
    <Upload className="mb-3 h-10 w-10 text-muted-foreground dark:text-gray-400" />;

  return (
    <div
      className={cn('flex w-full items-center justify-center', containerClassName)}
      data-testid={dataTestId}
    >
      <label
        htmlFor={resolvedInputId}
        className={cn(
          'flex h-40 w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed bg-muted/30 transition-colors hover:bg-muted/50 dark:border-gray-600 dark:bg-gray-700 dark:hover:bg-gray-600',
          disabled && 'cursor-not-allowed opacity-50',
          className,
        )}
        {...labelProps}
      >
        {children ?? (
          <div className="flex flex-col items-center justify-center pt-5 pb-6 text-center">
            {renderedIcon}
            <p className="mb-2 text-sm text-muted-foreground dark:text-gray-400">
              <span className="font-semibold">{primaryText}</span>
              {secondaryText ? ` ${secondaryText}` : ''}
            </p>
            {helperText && (
              <p className="text-xs text-muted-foreground dark:text-gray-400">
                {helperText}
              </p>
            )}
          </div>
        )}
        <input
          id={resolvedInputId}
          type="file"
          className="hidden"
          accept={accept}
          multiple={multiple}
          onChange={onChange}
          disabled={disabled}
          {...inputProps}
        />
      </label>
    </div>
  );
};

export default ImageUploadDropzone;
